<ng-container *ngIf="visible()">
  <button
    type="button"
    class="aca-ai-fab"
    [class.aca-ai-fab--open]="open()"
    (click)="toggle()"
    [attr.aria-expanded]="open()"
    [attr.aria-label]="(open() ? 'DMS.ASSISTANT.CLOSE' : 'DMS.ASSISTANT.OPEN') | translate"
  >
    <mat-icon>{{ open() ? 'close' : 'forum' }}</mat-icon>
  </button>

  <section class="aca-ai-pop" *ngIf="open()" role="dialog" [attr.aria-label]="'DMS.ASSISTANT.TITLE' | translate">
    <header class="aca-ai-pop__head">
      <span class="aca-ai-pop__mark" aria-hidden="true"><mat-icon>auto_awesome</mat-icon></span>
      <div class="aca-ai-pop__title">
        <strong>{{ 'DMS.ASSISTANT.TITLE' | translate }}</strong>
        <span *ngIf="status()?.provider as p">
          <i class="aca-ai-pop__where" [class.aca-ai-pop__where--cloud]="p.location === 'cloud'"></i>{{ p.model }} ·
          {{ (p.location === 'local' ? 'DMS.ASSISTANT.LOCAL' : 'DMS.ASSISTANT.CLOUD') | translate }}
        </span>
      </div>
      <button
        type="button"
        class="aca-ai-pop__icon"
        [class.aca-ai-pop__icon--on]="view() === 'history'"
        (click)="showHistory()"
        [matTooltip]="'DMS.ASSISTANT.HISTORY' | translate"
        [attr.aria-label]="'DMS.ASSISTANT.HISTORY' | translate"
      >
        <mat-icon>history</mat-icon>
      </button>
      <button
        type="button"
        class="aca-ai-pop__icon"
        (click)="newChat()"
        [disabled]="busy()"
        [matTooltip]="'DMS.ASSISTANT.NEW_CHAT' | translate"
        [attr.aria-label]="'DMS.ASSISTANT.NEW_CHAT' | translate"
      >
        <mat-icon>edit_square</mat-icon>
      </button>
      <button type="button" class="aca-ai-pop__icon" (click)="close()" [attr.aria-label]="'DMS.ASSISTANT.CLOSE' | translate">
        <mat-icon>expand_more</mat-icon>
      </button>
    </header>

    <div class="aca-ai-pop__body" #body aria-live="polite">
      <!-- ── history ───────────────────────────────────────────────── -->
      <div class="aca-ai-hist" *ngIf="view() === 'history'">
        <p class="aca-ai-hist__title">{{ 'DMS.ASSISTANT.HISTORY_TITLE' | translate }}</p>
        <p class="aca-ai-hist__empty" *ngIf="history() === null">{{ 'DMS.ASSISTANT.LOADING' | translate }}</p>
        <p class="aca-ai-hist__empty" *ngIf="history()?.length === 0">{{ 'DMS.ASSISTANT.HISTORY_EMPTY' | translate }}</p>
        <button
          type="button"
          class="aca-ai-hist__item"
          *ngFor="let c of history()"
          (click)="loadConversation(c.id)"
          [class.aca-ai-hist__item--on]="c.id === conversationId()"
        >
          <mat-icon aria-hidden="true">chat_bubble_outline</mat-icon>
          <span class="aca-ai-hist__text">
            <span class="aca-ai-hist__name">{{ c.title }}</span>
            <span class="aca-ai-hist__when"
              >{{ c.updatedAt | date: 'd MMM y HH:mm' }} · {{ 'DMS.ASSISTANT.TURNS' | translate: { count: c.turnCount } }}</span
            >
          </span>
          <span
            class="aca-ai-hist__del"
            role="button"
            tabindex="0"
            (click)="deleteConversation(c.id, $event)"
            (keydown.enter)="deleteConversation(c.id, $event)"
            [attr.aria-label]="'DMS.ASSISTANT.DELETE' | translate"
          >
            <mat-icon>delete_outline</mat-icon>
          </span>
        </button>
      </div>

      <!-- ── chat ──────────────────────────────────────────────────── -->
      <ng-container *ngIf="view() === 'chat'">
        <div class="aca-ai-hello" *ngIf="!turns().length">
          <span class="aca-ai-hello__mark" aria-hidden="true"><mat-icon>auto_awesome</mat-icon></span>
          <strong>{{ 'DMS.ASSISTANT.HELLO_TITLE' | translate }}</strong>
          <span>{{ 'DMS.ASSISTANT.HELLO_TEXT' | translate }}</span>
          <div class="aca-ai-hello__suggest">
            <ng-container *ngIf="documentId() && aboutDocument(); else general">
              <button type="button" *ngFor="let s of documentSuggestions" (click)="askKey(s)">{{ s | translate }}</button>
            </ng-container>
            <ng-template #general>
              <button type="button" *ngFor="let s of suggestions" (click)="askKey(s)">{{ s | translate }}</button>
            </ng-template>
          </div>
        </div>

        <article class="aca-ai-turn" *ngFor="let t of turns()">
          <p class="aca-ai-turn__q">
            <span class="aca-ai-turn__about" *ngIf="t.aboutDocument"
              ><mat-icon>description</mat-icon>{{ 'DMS.ASSISTANT.ABOUT_DOC' | translate }}</span
            >
            {{ t.question }}
          </p>

          <ol class="aca-ai-run" *ngIf="t.steps.length">
            <li class="aca-ai-run__step" *ngFor="let s of t.steps" [ngClass]="'aca-ai-run__step--' + s.state" [attr.data-kind]="s.kind">
              <span class="aca-ai-run__dot" aria-hidden="true"></span>
              <span class="aca-ai-run__main">
                <span class="aca-ai-run__head">
                  <strong>{{ s.title }}</strong>
                  <code *ngIf="s.sig">{{ s.sig }}</code>
                  <em *ngIf="s.ms !== undefined">{{ s.ms / 1000 | number: '1.1-1' }} s</em>
                </span>
                <span class="aca-ai-run__out">
                  {{
                    s.state === 'run'
                      ? ('DMS.ASSISTANT.WORKING' | translate)
                      : s.state === 'stopped'
                        ? ('DMS.ASSISTANT.STOPPED' | translate)
                        : s.summary
                  }}
                </span>
                <span class="aca-ai-run__line" *ngFor="let line of s.lines">{{ line }}</span>
              </span>
            </li>
          </ol>

          <div class="aca-ai-turn__a" *ngIf="t.answer" [class.aca-ai-turn__a--live]="t.state === 'run'">
            <ng-container *ngFor="let p of pieces(t); trackBy: trackByIndex">
              <ng-container *ngIf="p.text">{{ p.text }}</ng-container>
              <button
                type="button"
                class="aca-ai-cite"
                *ngIf="p.cite as c"
                (click)="openDocument(c.nodeId)"
                [matTooltip]="c.name + (c.page ? ' · ' + ('DMS.ASSISTANT.PAGE' | translate: { page: c.page }) : '')"
              >
                {{ c.n }}
              </button>
              <span class="aca-ai-cite aca-ai-cite--pending" *ngIf="p.pending">·</span>
            </ng-container>
          </div>

          <div class="aca-ai-turn__src" *ngIf="t.citations.length && t.state !== 'run'">
            <span class="aca-ai-turn__srctitle">{{
              (t.citations[0].inline === false ? 'DMS.ASSISTANT.READ_SOURCES' : 'DMS.ASSISTANT.SOURCES') | translate
            }}</span>
            <button type="button" class="aca-ai-src" *ngFor="let s of sources(t); trackBy: trackBySource" (click)="openDocument(s.nodeId)">
              <span class="aca-ai-src__n">{{ s.n }}</span>
              <span class="aca-ai-src__name">{{ s.name }}</span>
              <span class="aca-ai-src__pages" *ngIf="s.pages.length">{{ 'DMS.ASSISTANT.PAGE' | translate: { page: s.pages.join(', ') } }}</span>
              <mat-icon aria-hidden="true">open_in_new</mat-icon>
            </button>
          </div>

          <p class="aca-ai-turn__err" *ngIf="t.error"><mat-icon>error_outline</mat-icon>{{ t.error }}</p>
          <p class="aca-ai-turn__meta" *ngIf="t.state === 'stopped'">{{ 'DMS.ASSISTANT.STOPPED' | translate }}</p>
          <p class="aca-ai-turn__meta" *ngIf="t.meta">{{ t.meta }}</p>
        </article>
      </ng-container>
    </div>

    <form class="aca-ai-pop__composer" (submit)="$event.preventDefault(); ask()">
      <button
        type="button"
        class="aca-ai-scope"
        *ngIf="documentId()"
        (click)="aboutDocument.set(!aboutDocument())"
        [class.aca-ai-scope--doc]="aboutDocument()"
        [disabled]="busy()"
      >
        <mat-icon>{{ aboutDocument() ? 'description' : 'folder_open' }}</mat-icon>
        {{ (aboutDocument() ? 'DMS.ASSISTANT.SCOPE_DOC' : 'DMS.ASSISTANT.SCOPE_ALL') | translate }}
      </button>
      <div class="aca-ai-pop__ask">
        <textarea
          name="question"
          rows="1"
          [(ngModel)]="question"
          (keydown.enter)="onEnter($event)"
          [placeholder]="(documentId() && aboutDocument() ? 'DMS.ASSISTANT.ASK_DOC' : 'DMS.ASSISTANT.ASK') | translate"
          [attr.aria-label]="'DMS.ASSISTANT.ASK' | translate"
        ></textarea>
        <button
          type="button"
          class="aca-ai-pop__send aca-ai-pop__send--stop"
          *ngIf="busy()"
          (click)="stop()"
          [attr.aria-label]="'DMS.ASSISTANT.STOP' | translate"
        >
          <mat-icon>stop</mat-icon>
        </button>
        <button
          type="submit"
          class="aca-ai-pop__send"
          *ngIf="!busy()"
          [disabled]="!question.trim()"
          [attr.aria-label]="'DMS.ASSISTANT.SEND' | translate"
        >
          <mat-icon>arrow_upward</mat-icon>
        </button>
      </div>
      <p class="aca-ai-pop__foot">{{ 'DMS.ASSISTANT.FOOT' | translate }}</p>
    </form>
  </section>
</ng-container>
